import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, CheckCircle2, ChevronDown, Loader2, X } from "lucide-react";
import { submitEnterpriseLead } from "../enterpriseLead";
import { cn, ProductLogo, useDismiss } from "./shared";

const BUSINESS_TYPES = [
  "Car Rental Website (you own all vehicles)",
  "Peer to Peer/Car Sharing (mix of vehicle owners)",
  "Gig economy/Rideshare/Delivery",
  "Subscription/Long Term/Short Term",
  "Auto Dealership",
  "Service & Repair",
  "Fleet Management Platform",
  "Rental Management Platform",
  "Finance Industry",
  "Insurance Industry",
  "Other (specify in notes)",
];

const PRODUCTS = [
  "On-Rent Commercial Auto Insurance",
  "Roadside Assistance Program",
  "Tolls & Tickets Automation",
  "ID & Biometrics",
  "Driver's License Status Check",
  "Criminal Background Check",
  "Motor Vehicle Record Check",
  "Auto Insurance Validation Check",
  "Platform & Marketplace Infrastructure",
];

const PRODUCT_NAMES = ["RentShield", "RentID", "RentSuccess", "RentDirect"];

/* `ent-input` (enterprise.css) carries the border colours: Bootstrap's !important `.border`
   shorthand would otherwise win over the focus/invalid variants. */
const inputClass =
  "ent-input h-11 w-full rounded-xl border bg-card px-3.5 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring/15";

function Field({ id, label, required, error, className, children }) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-muted-foreground">{" *"}</span>}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/* Checkbox list in a popover (stands in for v0's Radix Popover). */
function MultiSelect({ name, label, options, invalid }) {
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const rootRef = useDismiss(open, close);
  const toggle = (type) =>
    setSelected((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));

  return (
    <div ref={rootRef} className="relative">
      {selected.map((type) => (
        <input key={type} type="hidden" name={name} value={type} />
      ))}
      <button
        type="button"
        id={`ent-${name}`}
        aria-expanded={open}
        data-invalid={invalid || undefined}
        aria-describedby={invalid ? `ent-${name}-error` : undefined}
        onClick={() => setOpen((o) => !o)}
        className={cn(inputClass, "flex items-center justify-between gap-3 text-left")}
      >
        <span className={cn("truncate", selected.length === 0 && "text-muted-foreground/70")}>
          {selected.length === 0 ? "Select all that apply" : selected.join(", ")}
        </span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
      {open && (
        <div className="ent-pop-in absolute left-0 right-0 top-full z-30 mt-1 rounded-xl border border-border bg-card p-1.5 shadow-md">
          <div role="group" aria-label={label} className="flex max-h-80 flex-col overflow-y-auto">
            {options.map((type) => (
              <label
                key={type}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground transition-colors hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(type)}
                  onChange={() => toggle(type)}
                  className="size-4 shrink-0 accent-foreground"
                />
                {type}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <fieldset className="flex flex-col gap-4 border-t border-border pt-7 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground" aria-hidden="true">
        {title}
      </p>
      {children}
    </fieldset>
  );
}

export function EnterpriseLeadForm() {
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [hasReferral, setHasReferral] = useState(false);
  const [pending, setPending] = useState(false);
  // Spam check: when the form appeared (see submitEnterpriseLead).
  const startedAt = useRef(Date.now());

  async function handleSubmit(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const get = (key) => String(data.get(key) ?? "").trim();

    const lead = {
      firstName: get("firstName"),
      lastName: get("lastName"),
      businessName: get("businessName"),
      email: get("email"),
      phone: get("phone"),
      website: get("website"),
      states: get("states"),
      fleetSize: get("fleetSize"),
      referralSource: get("referralSource"),
      businessTypes: data.getAll("businessTypes").map(String),
      products: data.getAll("products").map(String),
      message: get("message"),
      consent: Boolean(data.get("consent")),
      honeypot: get("faxNumber"),
      startedAt: startedAt.current,
    };

    const nextErrors = {};
    if (!lead.firstName) nextErrors.firstName = "First name is required.";
    if (!lead.lastName) nextErrors.lastName = "Last name is required.";
    if (!lead.businessName) nextErrors.businessName = "Business name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) nextErrors.email = "Enter a valid work email.";
    if (!lead.states) nextErrors.states = "Tell us where you operate.";
    if (!lead.fleetSize) nextErrors.fleetSize = "Enter your fleet size.";
    if (!lead.referralSource) nextErrors.referralSource = "Let us know how you heard about us.";
    if (hasReferral) {
      if (lead.businessTypes.length === 0) nextErrors.businessTypes = "Select at least one business type.";
      if (lead.products.length === 0) nextErrors.products = "Select at least one product.";
      if (!data.get("consent")) nextErrors.consent = "Please confirm we may contact you.";
    }
    setErrors(nextErrors);
    setSubmitError(null);

    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      document.getElementById(`ent-${firstInvalid}`)?.focus();
      return;
    }

    setPending(true);
    try {
      const result = await submitEnterpriseLead(lead);
      if (result.ok) setSubmitted(true);
      else setSubmitError(result.error);
    } catch {
      setSubmitError("Something went wrong. Please try again in a moment.");
    } finally {
      setPending(false);
    }
  }

  const errorProps = (key) => (errors[key] ? { "aria-invalid": true, "aria-describedby": `ent-${key}-error` } : {});

  return (
    <div>
      {submitted ? (
        <div
          role="status"
          className="flex min-h-80 flex-col items-start justify-center gap-4 rounded-3xl border border-border bg-surface-subtle p-8 sm:p-10"
        >
          <CheckCircle2 className="size-10 text-foreground" aria-hidden="true" />
          <h3 className="font-heading text-2xl font-semibold tracking-tight text-foreground">Thanks — your request is in.</h3>
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
            A member of the Rentid enterprise team will reach out shortly to talk through your operation and the right
            products for your fleet.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-7" aria-label="Enterprise sales inquiry">
          {/* Honeypot: off-screen and skipped by keyboard and screen readers, so only bots fill it in. */}
          <div aria-hidden="true" className="ent-honeypot">
            <label htmlFor="ent-faxNumber">Fax number</label>
            <input id="ent-faxNumber" name="faxNumber" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
          </div>
          <FormSection title="Contact information">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="ent-firstName" label="First Name" required error={errors.firstName}>
                <input id="ent-firstName" name="firstName" autoComplete="given-name" className={inputClass} {...errorProps("firstName")} />
              </Field>
              <Field id="ent-lastName" label="Last Name" required error={errors.lastName}>
                <input id="ent-lastName" name="lastName" autoComplete="family-name" className={inputClass} {...errorProps("lastName")} />
              </Field>
              <Field id="ent-businessName" label="Business Name" required error={errors.businessName}>
                <input id="ent-businessName" name="businessName" autoComplete="organization" className={inputClass} {...errorProps("businessName")} />
              </Field>
              <Field id="ent-email" label="Work Email" required error={errors.email}>
                <input id="ent-email" name="email" type="email" autoComplete="email" className={inputClass} {...errorProps("email")} />
              </Field>
              <Field id="ent-phone" label="Phone Number">
                <input id="ent-phone" name="phone" type="tel" autoComplete="tel" className={inputClass} />
              </Field>
              <Field id="ent-website" label="Company Website">
                <input id="ent-website" name="website" type="url" inputMode="url" placeholder="https://" autoComplete="url" className={inputClass} />
              </Field>
              <Field id="ent-states" label="State(s) of Operation" required error={errors.states}>
                <input id="ent-states" name="states" placeholder="e.g. FL, TX, CA" className={inputClass} {...errorProps("states")} />
              </Field>
              <Field id="ent-fleetSize" label="Fleet Size" required error={errors.fleetSize}>
                <input
                  id="ent-fleetSize"
                  name="fleetSize"
                  inputMode="numeric"
                  placeholder="e.g. 120 vehicles"
                  className={inputClass}
                  {...errorProps("fleetSize")}
                />
              </Field>
              <Field id="ent-referralSource" label="How did you hear about us?" required error={errors.referralSource} className="sm:col-span-2">
                <input
                  id="ent-referralSource"
                  name="referralSource"
                  className={inputClass}
                  onChange={(e) => setHasReferral(e.target.value.trim().length > 0)}
                  {...errorProps("referralSource")}
                />
              </Field>
            </div>
          </FormSection>

          {/* Revealed once "How did you hear about us?" is filled in (kept mounted so selections survive). */}
          <div className={cn("ent-slide-in flex flex-col gap-7", !hasReferral && "hidden")}>
            <FormSection title="Business & products">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="ent-businessTypes" label="Business Type" required error={errors.businessTypes}>
                  <MultiSelect name="businessTypes" label="Business type" options={BUSINESS_TYPES} invalid={!!errors.businessTypes} />
                </Field>
                <Field id="ent-products" label="Products interested in" required error={errors.products}>
                  <MultiSelect name="products" label="Products interested in" options={PRODUCTS} invalid={!!errors.products} />
                </Field>
              </div>
            </FormSection>

            <FormSection title="Additional information">
              <Field id="ent-message" label="What are you looking to solve or integrate?">
                <textarea id="ent-message" name="message" rows={4} className={cn(inputClass, "h-auto resize-y py-3 leading-relaxed")} />
              </Field>
            </FormSection>
          </div>

          {hasReferral && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="ent-consent" className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-foreground">
                <input
                  id="ent-consent"
                  name="consent"
                  type="checkbox"
                  className="mt-1 size-4 shrink-0 accent-foreground"
                  {...errorProps("consent")}
                />
                <span>
                  I confirm that Rentid Inc. may contact me using the information I provided.
                  <span className="text-muted-foreground">{" *"}</span>
                </span>
              </label>
              {errors.consent && (
                <p id="ent-consent-error" className="text-xs text-destructive">
                  {errors.consent}
                </p>
              )}
            </div>
          )}

          {submitError && (
            <p role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {submitError}
            </p>
          )}

          <button
            type="submit"
            disabled={pending}
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-full ent-cta bg-primary px-7 text-sm font-semibold text-primary-foreground transition-colors disabled:opacity-60 sm:self-end"
          >
            {pending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Sending…
              </>
            ) : (
              <>
                {hasReferral ? "Submit Application" : "Next"}
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

/* Modal (stands in for v0's Radix Dialog). Portalled to <body> inside its own
   `.rd-home.rd-ent` wrapper so the scoped Tailwind build and tokens still apply. */
function EnterpriseSalesDialog({ open, onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="rd-home rd-ent ent-portal">
      <div className="ent-overlay-in ent-dialog-layer fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="ent-dialog-layer pointer-events-none fixed inset-0 flex items-center justify-center p-4">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ent-sales-title"
          aria-describedby="ent-sales-description"
          tabIndex={-1}
          className="ent-dialog-in pointer-events-auto relative max-h-[90dvh] w-full overflow-y-auto rounded-3xl border border-border bg-background p-6 shadow-lg outline-none sm:max-w-3xl sm:p-10"
        >
          <div className="mb-4 flex flex-col gap-2 text-left">
            <h2 id="ent-sales-title" className="font-heading text-3xl font-semibold tracking-tighter text-foreground">
              Contact Enterprise Sales
            </h2>
            <p id="ent-sales-description" className="text-sm text-muted-foreground">
              {"Tell us about your operation and we'll help you pick the right Rentid APIs."}
            </p>
          </div>
          <EnterpriseLeadForm />
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm text-foreground opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="size-4" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function EnterpriseContact() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  return (
    <section id="contact" className="scroll-mt-8 border-t border-border bg-surface-subtle">
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 px-4 py-24 text-center sm:px-6 md:py-32">
        <h2 className="font-heading text-4xl font-semibold leading-[1.02] tracking-tighter text-foreground text-balance sm:text-5xl">
          Build fast. Connect easily. Get rented.
        </h2>
        <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground text-pretty">
          Enterprise level mobility services for your platform.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 py-4">
          {PRODUCT_NAMES.map((name) => (
            <ProductLogo key={name} name={name} className="h-14 w-auto" />
          ))}
        </div>
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => setDialogOpen(true)}
          className="group inline-flex h-12 items-center justify-center gap-2 rounded-full ent-cta bg-primary px-7 text-sm font-semibold text-primary-foreground transition-colors"
        >
          Contact Enterprise Sales
          <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
        <EnterpriseSalesDialog open={dialogOpen} onClose={closeDialog} />
      </div>
      <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 md:pb-20">
        <div className="flex flex-col gap-3 border-t border-border pt-8 text-xs leading-relaxed text-muted-foreground/80">
          <h3 className="font-semibold text-muted-foreground">Partnership Program Disclaimer</h3>
          <p>
            Participation in the Rentid Partnership Program is subject to underwriting, eligibility, operational,
            compliance, and other approval criteria established by Rentid and its applicable partners.
          </p>
          <p>
            Submitting an application does not guarantee acceptance into the Partnership Program. Not all applicants
            will be approved or selected to participate. Underwriting review is a required part of the partnership
            approval process and may include review of business operations, fleet profile, claims or loss history, risk
            characteristics, compliance requirements, and other relevant information.
          </p>
          <p>
            Applicants who are not approved may reapply after 30 days, subject to the eligibility requirements and
            approval criteria in effect at the time of reapplication.
          </p>
          <p>
            Rentid reserves the right to approve, decline, limit, suspend, or discontinue participation in the
            Partnership Program based on applicable program requirements, underwriting considerations, partner
            requirements, or other business and risk factors.
          </p>
        </div>
      </div>
    </section>
  );
}
